import React, { useState } from 'react';
import { Button, Form } from 'antd';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';

const FUNCTIONS_BASE_URL = process.env.REACT_APP_FUNCTIONS_BASE_URL;

const FRIENDLY_ERRORS = {
  'auth/invalid-credential': 'Incorrect email or password. Please check your onboarding email and try again.',
  'auth/user-disabled': 'This account has been disabled. Please contact support@thetalentmapper.com.',
  'auth/too-many-requests': 'Too many failed attempts. Please wait a few minutes and try again.',
};

// Inline SVG icons
const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 13, height: 13, stroke: 'var(--orange)' }}>
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 15, height: 15, color: '#bbb', flexShrink: 0 }}>
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);

const PwIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 15, height: 15, color: '#bbb', flexShrink: 0 }}>
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const EyeIcon = ({ visible }) => visible ? (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 16, height: 16 }}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
) : (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 16, height: 16 }}>
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const ErrorIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 15, height: 15, flexShrink: 0 }}>
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const CheckCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 36, height: 36, stroke: 'var(--orange)' }}>
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const BackIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ width: 14, height: 14 }}>
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

function Login() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);

  // Forgot password state
  const [view, setView] = useState('login'); // 'login' | 'forgot'
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError(FRIENDLY_ERRORS[err.code] || 'Sign-in failed. Please try again or contact support@thetalentmapper.com.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail || !FUNCTIONS_BASE_URL) {
      setResetSent(true);
      return;
    }
    setResetLoading(true);
    try {
      await fetch(`${FUNCTIONS_BASE_URL}/sendPasswordResetEmail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail.trim() }),
      });
    } catch {
      // Intentionally swallowed — always show success to prevent enumeration
    }
    setResetSent(true);
    setResetLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundImage: 'url(/assets/Art.png)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      padding: '20px',
    }}>
      {/* Card */}
      <div style={{
        background: 'var(--white)',
        width: '100%',
        maxWidth: 420,
        borderRadius: 6,
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(33,33,33,.18)',
        animation: 'fadeUp .35s var(--ease) both',
      }}>
        <div style={{ padding: '44px 48px 40px' }}>
          {/* Logo */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
            <img src="/assets/default-logo.svg" alt="TalentMapper" style={{ height: 26, width: 'auto', display: 'block' }} />
          </div>

          {view === 'forgot' ? (
            /* ── Forgot password view ── */
            <div>
              <button
                type="button"
                onClick={() => { setView('login'); setResetSent(false); }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--mid)',
                  padding: 0, marginBottom: 20,
                }}
              >
                <BackIcon /> Back to sign in
              </button>

              {resetSent ? (
                <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
                  <div style={{
                    width: 56, height: 56, background: 'var(--almond)', borderRadius: '50%',
                    margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <CheckCircleIcon />
                  </div>
                  <h3 style={{
                    fontFamily: 'var(--serif)', fontSize: 18, fontWeight: 600,
                    color: 'var(--black)', marginBottom: 10, marginTop: 0,
                  }}>
                    Check your inbox
                  </h3>
                  <p style={{ fontSize: 14, color: 'var(--mid)', lineHeight: 1.65, marginBottom: 20 }}>
                    If an account exists for <strong>{resetEmail}</strong>, a password reset link
                    will arrive shortly from <strong>hello@thetalentmapper.com</strong>.
                  </p>
                  <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.55, marginBottom: 0 }}>
                    Didn't receive it? Check your spam folder or contact{' '}
                    <a href="mailto:support@thetalentmapper.com" style={{ color: 'var(--orange)', textDecoration: 'none' }}>
                      support@thetalentmapper.com
                    </a>.
                  </p>
                </div>
              ) : (
                <>
                  <h2 style={{
                    fontFamily: 'var(--serif)', fontSize: 20, fontWeight: 600,
                    color: 'var(--black)', marginBottom: 8, marginTop: 0,
                  }}>
                    Reset your password
                  </h2>
                  <p style={{ fontSize: 14, color: 'var(--mid)', lineHeight: 1.6, marginBottom: 20 }}>
                    Enter your email address and we'll send you a link to set a new password.
                  </p>

                  <form onSubmit={handleResetPassword} noValidate>
                    <div style={{ marginBottom: 20 }}>
                      <label style={{
                        display: 'block', fontSize: 11, fontWeight: 600,
                        color: 'var(--muted)', letterSpacing: '.6px', textTransform: 'uppercase',
                        marginBottom: 6,
                      }}>
                        Email address
                      </label>
                      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <span style={{ position: 'absolute', left: 12, display: 'flex', pointerEvents: 'none' }}>
                          <MailIcon />
                        </span>
                        <input
                          type="email"
                          value={resetEmail}
                          onChange={(e) => setResetEmail(e.target.value)}
                          placeholder="you@company.com"
                          autoComplete="email"
                          required
                          style={{
                            width: '100%', fontFamily: 'var(--sans)', fontSize: 14, color: 'var(--black)',
                            padding: '10px 12px 10px 38px',
                            border: '1.5px solid var(--line)', borderRadius: 3,
                            background: 'var(--white)', outline: 'none',
                          }}
                          onFocus={(e) => { e.target.style.borderColor = 'var(--orange)'; }}
                          onBlur={(e) => { e.target.style.borderColor = 'var(--line)'; }}
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={resetLoading || !resetEmail}
                      style={{
                        width: '100%', padding: 13,
                        fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 600,
                        background: resetLoading || !resetEmail ? '#f0c4a0' : 'var(--orange)',
                        color: 'var(--white)',
                        border: 'none', borderRadius: 3,
                        cursor: resetLoading || !resetEmail ? 'default' : 'pointer',
                        letterSpacing: '.2px', transition: 'background .15s',
                      }}
                    >
                      {resetLoading ? 'Sending…' : 'Send reset link'}
                    </button>
                  </form>
                </>
              )}
            </div>
          ) : (
            /* ── Sign in view ── */
            <>
              {/* Security chip */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: 'var(--almond)', borderRadius: 20,
                  padding: '5px 12px', fontSize: 12, color: 'var(--black)',
                }}>
                  <LockIcon />
                  Secure File Dropper
                </span>
              </div>

          {/* Error banner */}
          {error && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'var(--err-bg)', borderRadius: 3,
              padding: '10px 12px', fontSize: 13, color: 'var(--err-txt)',
              marginBottom: 16,
            }}>
              <ErrorIcon />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {/* Email field */}
            <div style={{ marginBottom: 16 }}>
              <label style={{
                display: 'block', fontSize: 11, fontWeight: 600,
                color: 'var(--muted)', letterSpacing: '.6px', textTransform: 'uppercase',
                marginBottom: 6,
              }}>
                Email address
              </label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <span style={{ position: 'absolute', left: 12, display: 'flex', pointerEvents: 'none' }}>
                  <MailIcon />
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  autoComplete="email"
                  required
                  style={{
                    width: '100%', fontFamily: 'var(--sans)', fontSize: 14, color: 'var(--black)',
                    padding: '10px 12px 10px 38px',
                    border: '1.5px solid var(--line)', borderRadius: 3,
                    background: 'var(--white)', outline: 'none',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--orange)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'var(--line)'; }}
                />
              </div>
            </div>

            {/* Password field */}
            <div style={{ marginBottom: 24 }}>
              <label style={{
                display: 'block', fontSize: 11, fontWeight: 600,
                color: 'var(--muted)', letterSpacing: '.6px', textTransform: 'uppercase',
                marginBottom: 6,
              }}>
                Password
              </label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <span style={{ position: 'absolute', left: 12, display: 'flex', pointerEvents: 'none' }}>
                  <PwIcon />
                </span>
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  autoComplete="current-password"
                  required
                  style={{
                    width: '100%', fontFamily: 'var(--sans)', fontSize: 14, color: 'var(--black)',
                    padding: '10px 44px 10px 38px',
                    border: '1.5px solid var(--line)', borderRadius: 3,
                    background: 'var(--white)', outline: 'none',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--orange)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'var(--line)'; }}
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  style={{
                    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                    color: 'var(--muted)', display: 'flex', alignItems: 'center',
                  }}
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                >
                  <EyeIcon visible={showPw} />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !email || !password}
              style={{
                width: '100%', padding: 13,
                fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 600,
                background: loading || !email || !password ? '#f0c4a0' : 'var(--orange)',
                color: 'var(--white)',
                border: 'none', borderRadius: 3,
                cursor: loading || !email || !password ? 'default' : 'pointer',
                letterSpacing: '.2px',
                transition: 'background .15s',
              }}
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>

            <div style={{ textAlign: 'center', marginTop: 14 }}>
              <button
                type="button"
                onClick={() => { setView('forgot'); setResetEmail(email); setResetSent(false); }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--orange)',
                  padding: 0, textDecoration: 'underline',
                  transition: 'color .15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#c44f00'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--orange)'; }}
              >
                Forgot password?
              </button>
            </div>
          </form>

          {/* Credentials hint */}
          <div style={{
            marginTop: 20, padding: '12px 16px',
            background: 'var(--almond)', borderRadius: 4,
            fontSize: 13, color: 'var(--black)', lineHeight: 1.5,
          }}>
            Your credentials were sent in your onboarding email. If you need help, contact{' '}
            <a href="mailto:support@thetalentmapper.com" style={{ color: 'var(--orange)', textDecoration: 'none' }}>
              support@thetalentmapper.com
            </a>
          </div>
            </>
          )}
        </div>

        {/* DPA footer */}
        <div style={{
          padding: '16px 24px 20px',
          borderTop: '1px solid var(--line)',
        }}>
          <div style={{
            fontSize: 12, color: 'var(--mid)',
            lineHeight: 1.6, marginBottom: 10,
            padding: '10px 14px',
            background: 'var(--white)',
            borderRadius: 4,
            borderTop: '2px solid var(--orange)',
          }}>
            By uploading files you confirm you are authorised to share this data with TalentMapper and consent to it being
            processed in accordance with our{' '}
            <a href="https://thetalentmapper.com/privacy-policy" target="_blank" rel="noopener noreferrer"
              style={{ color: 'var(--orange)', textDecoration: 'none' }}>Privacy Policy</a>
            {' '}and{' '}
            <a href="https://thetalentmapper.com/terms-conditions" target="_blank" rel="noopener noreferrer"
              style={{ color: 'var(--orange)', textDecoration: 'none' }}>Data Processing Agreement</a>.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '4px 12px', marginBottom: 8 }}>
            <a href="https://thetalentmapper.com/terms-conditions" target="_blank" rel="noopener noreferrer"
              style={{ fontSize: 12, color: 'var(--muted)', textDecoration: 'none' }}>T&amp;Cs</a>
            <span style={{ fontSize: 12, color: 'var(--line)' }}>·</span>
            <a href="https://thetalentmapper.com/privacy-policy" target="_blank" rel="noopener noreferrer"
              style={{ fontSize: 12, color: 'var(--muted)', textDecoration: 'none' }}>Privacy Policy</a>
            <span style={{ fontSize: 12, color: 'var(--line)' }}>·</span>
            <a href="https://thetalentmapper.com/cookies" target="_blank" rel="noopener noreferrer"
              style={{ fontSize: 12, color: 'var(--muted)', textDecoration: 'none' }}>Cookies</a>
          </div>
          <p style={{ fontSize: 11, color: 'var(--faint)', textAlign: 'center', margin: 0, lineHeight: 1.7 }}>
            TalentMapper Limited · Company No. 14476153 · Registered in England &amp; Wales
          </p>
        </div>
      </div>
    </div>
  );
}

// Keep Form imported to avoid breaking antd tree-shaking, but it isn't used directly
void Form;

export default Login;

